import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Search, Send, Zap, CheckCircle, Clock, Loader, Paperclip, Mic, ArrowLeft, ChevronDown, ChevronUp, BarChart3, ArrowUpRight, Shield, Bot, Check, ClipboardList } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', sally: 'Sally', marsi: 'Marsi', zira: 'Zira', tali: 'Tali', finn: 'Finn', buzz: 'Buzz', wright: 'Wright', dora: 'Dora' };
const STATUS_COLOR = {
  green: { ring: '#34d399', text: 'text-emerald-400', lightText: 'text-emerald-600' },
  amber: { ring: '#fbbf24', text: 'text-amber-400', lightText: 'text-amber-600' },
  red: { ring: '#f87171', text: 'text-red-400', lightText: 'text-red-500' },
};
const ACTION_STATUS = {
  'complete': { icon: CheckCircle, dark: 'text-emerald-400', light: 'text-emerald-600' },
  'in-progress': { icon: Loader, dark: 'text-blue-400', light: 'text-blue-600' },
  'pending-approval': { icon: Clock, dark: 'text-amber-400', light: 'text-amber-600' },
  'recommended': { icon: Zap, dark: 'text-indigo-400', light: 'text-indigo-600' },
};

const ScoreRing = ({ score, size, strokeWidth, isDark, status = 'green' }) => {
  const r = (size - strokeWidth) / 2; const c = 2 * Math.PI * r; const p = (score / 100) * c; const s = STATUS_COLOR[status];
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={s.ring} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - p} className="transition-all duration-700" />
      </svg>
      <span className={`absolute text-lg font-bold ${isDark ? s.text : s.lightText}`}>{score}</span>
    </div>
  );
};

const AgentCard = ({ agent, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500'; const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`px-3 py-3 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`dept-agent-${agent.id}`}>
      <div className="flex items-center gap-2.5 mb-2">
        {AGENT_THUMBS[agent.id] && <img src={AGENT_THUMBS[agent.id]} alt="" className="w-8 h-8 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><p className={`text-xs font-semibold ${tp}`}>{agent.name}</p><span className={`w-2 h-2 rounded-full flex-shrink-0 ${agent.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} /></div>
          <p className={`text-[10px] ${ts}`}>{agent.role}</p>
        </div>
      </div>
      <p className={`text-[11px] leading-relaxed mb-1.5 ${tp}`}>{agent.currentTask}</p>
      <p className={`text-[10px] ${ts}`}>Last: {agent.lastAction} · {agent.lastActionTime}</p>
    </div>
  );
};

const AlertCard = ({ alert, isDark, onDeepDive, onEscalate, escalated, addressed, escalateLabel, deptHeadShortName }) => {
  const [expanded, setExpanded] = useState(false);
  const isHigh = alert.severity === 'high';
  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500'; const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = addressed ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isHigh ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'));
  const cardBorder = addressed ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : (isHigh ? (isDark ? 'border-red-500/15' : 'border-red-200/60') : border);
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const sevColor = isHigh ? (isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10') : (isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-700 bg-amber-700/10');
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-3`} data-testid={`dept-alert-${alert.id}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${addressed ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isHigh ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-amber-400' : 'text-amber-600'))}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><p className={`text-xs font-semibold flex-1 ${tp}`}>{alert.title}</p>
            {addressed ? (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}><CheckCircle className="w-2.5 h-2.5" /> Actioned · {deptHeadShortName} owns</span>
            ) : escalated ? (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-indigo-400 bg-indigo-400/10' : 'text-indigo-600 bg-indigo-600/10'}`}><ArrowUpRight className="w-2.5 h-2.5" /> Escalated</span>
            ) : (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${sevColor}`}>{isHigh ? 'High' : 'Medium'}</span>
            )}
          </div>
          <p className={`text-[10px] mt-1.5 leading-relaxed ${ts}`}>{alert.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-2.5 ml-5">
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`}>{expanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />} Details</button>
        {!addressed && (
          <button onClick={() => onDeepDive(alert)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}><Search className="w-2.5 h-2.5" /> Deep Dive</button>
        )}
        {!escalated && !addressed && escalateLabel && <button onClick={() => onEscalate(alert.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' : 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/15'}`}><ArrowUpRight className="w-2.5 h-2.5" /> {escalateLabel}</button>}
      </div>
      {expanded && (
        <div className={`mt-2.5 ml-5 p-2.5 rounded-md space-y-1.5 ${isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          {alert.actions.map((a, i) => { const st = ACTION_STATUS[a.status] || ACTION_STATUS['recommended']; const Icon = st.icon;
            return <div key={i} className="flex items-center gap-2"><Icon className={`w-3 h-3 flex-shrink-0 ${isDark ? st.dark : st.light}`} /><span className={`text-[10px] ${tp}`}>{a.label}</span><span className={`text-[9px] ${ts}`}>— {AGENT_NAMES[a.agent]}</span></div>;
          })}
        </div>
      )}
    </div>
  );
};

const DecisionCard = ({ item, isDark, onApprove, onDeepDive, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500'; const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = approved ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  return (
    <div className={`${cardBg} border ${approved ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : border} rounded-lg px-3 py-3`} data-testid={`dept-decision-${item.id}`}>
      <div className="flex items-start gap-2">
        <Shield className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><p className={`text-xs font-semibold flex-1 ${tp}`}>{item.title}</p>
            {item.urgency === 'high' && !approved && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Urgent</span>}
            {approved && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}><CheckCircle className="w-2.5 h-2.5" /> Approved</span>}
          </div>
          <p className={`text-[10px] mt-1.5 leading-relaxed ${ts}`}>{item.detail}</p>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">{AGENT_THUMBS[item.agent] && <img src={AGENT_THUMBS[item.agent]} alt="" className="w-4 h-4 rounded-full object-cover" />}<span className={`text-[9px] ${ts}`}>{AGENT_NAMES[item.agent]}</span></div>
            {!approved && (
              <div className="flex items-center gap-1.5">
                <button onClick={() => onDeepDive && onDeepDive(item.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'} transition-colors`} data-testid={`dept-decision-deep-dive-${item.id}`}><Search className="w-2.5 h-2.5" /> Deep Dive</button>
                <button onClick={() => onApprove(item.id)} className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'} transition-colors`} data-testid={`dept-approve-${item.id}`}><CheckCircle className="w-2.5 h-2.5" /> Approve</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const typeDot = item.type === 'alert' ? 'bg-red-400' : item.type === 'insight' ? 'bg-indigo-400' : 'bg-blue-400';
  return (
    <div className="flex items-start gap-2.5 py-2.5" data-testid={`dept-activity-${item.id}`}>
      {AGENT_THUMBS[item.agent] && <img src={AGENT_THUMBS[item.agent]} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0"><p className={`text-[11px] leading-relaxed ${tp}`}><span className="font-semibold">{AGENT_NAMES[item.agent]}</span> {item.action}</p><p className={`text-[9px] mt-0.5 ${ts}`}>{item.time}</p></div>
      <div className={`w-1.5 h-1.5 rounded-full ${typeDot} flex-shrink-0 mt-2`} />
    </div>
  );
};

/* ══════════ Chat Message (rich artifacts: thinking, chips, exec, routedTask, closure, complete-pill) ══════════ */
const ChatMessage = ({ msg, isDark, agentId, agentName, onChipClick, chipsDisabled }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500'; const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const thumb = AGENT_THUMBS[agentId];

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>{msg.text}</div>
      </div>
    );
  }

  if (msg.role === 'thinking') {
    return (
      <div className="flex gap-4 items-center">
        {thumb && <img src={thumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />}
        <div className="flex items-center gap-2.5">
          <span className={`text-[13px] italic ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{agentName} is thinking</span>
          <span className="flex gap-1">
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '0ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '150ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '300ms' }} />
          </span>
        </div>
      </div>
    );
  }

  if (msg.role === 'complete-pill') {
    return (
      <div className="flex justify-center">
        <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-medium ${isDark ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/15' : 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/15'}`}>
          <Check className="w-3 h-3" /> Conversation complete
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {thumb && <img src={thumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0 space-y-4">
        {msg.text && <div className={`text-[14px] leading-[1.8] whitespace-pre-line ${tp}`}>{msg.text}</div>}
        {msg.chips && !chipsDisabled && (
          <div className="flex flex-wrap gap-2 pt-3">
            {msg.chips.map((c) => (
              <button key={c.id} onClick={() => onChipClick && onChipClick(c)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium border transition-all ${isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-indigo-500/15 hover:border-indigo-500/30 hover:text-white' : 'bg-white border-black/[0.08] text-slate-700 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-slate-900'}`}
                data-testid={`dept-chip-${c.id}`}>{c.label}</button>
            ))}
          </div>
        )}
        {msg.executionSteps && (
          <div className={`rounded-xl px-4 py-3 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`}>
            <div className="space-y-2">
              {msg.executionSteps.map((step, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ animation: `deptFade 0.35s ease-out ${i * 0.25}s both` }}>
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-[13px] ${isDark ? 'text-white/75' : 'text-slate-700'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {msg.routedTask && (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`}>
            <div className={`px-4 py-2.5 border-b ${border} flex items-center gap-2 ${isDark ? 'bg-gradient-to-r from-purple-500/8 to-indigo-500/8' : 'bg-gradient-to-r from-purple-50 to-indigo-50'}`}>
              <ClipboardList className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Task routed to {msg.routedTask.headName}</span>
            </div>
            <div className="px-4 py-3">
              <p className={`text-[13px] font-semibold leading-snug ${tp}`}>{msg.routedTask.title}</p>
              <p className={`text-[12px] leading-relaxed mt-1 ${ts}`}>{msg.routedTask.context}</p>
              <div className={`flex items-center gap-3 mt-2.5 pt-2.5 border-t ${border}`}>
                <div className="flex items-center gap-1.5">
                  {AGENT_THUMBS[msg.routedTask.agent] && <img src={AGENT_THUMBS[msg.routedTask.agent]} alt="" className="w-4 h-4 rounded-full object-cover" />}
                  <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[msg.routedTask.agent] || msg.routedTask.agent}</span>
                </div>
                <div className="flex items-center gap-1"><Clock className={`w-3 h-3 ${ts}`} /><span className={`text-[11px] ${ts}`}>{msg.routedTask.dueDate}</span></div>
                <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-400/10 text-amber-400' : 'bg-amber-500/10 text-amber-600'}`}>Pending with {msg.routedTask.headName}</span>
              </div>
            </div>
          </div>
        )}
        {msg.closure && (
          <div className={`text-[13px] italic leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{msg.closure}</div>
        )}
        {msg.actions && (
          <div className="flex flex-col gap-2">
            {msg.actions.map((a, i) => {
              const st = ACTION_STATUS[a.status] || ACTION_STATUS['recommended']; const Icon = st.icon;
              return (
                <div key={i} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? st.dark : st.light}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] leading-relaxed ${tp}`}>{a.label}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {AGENT_THUMBS[a.agent] && <img src={AGENT_THUMBS[a.agent]} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
                      <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[a.agent]}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {msg.timeline && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10' : 'bg-indigo-500/5 border border-indigo-500/10'}`}>
            <Clock className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-[12px] leading-relaxed ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>{msg.timeline}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════ */
/* CEO Department Page Template          */
/* ══════════════════════════════════════ */

const V4CeoDeptTemplate = ({
  isDark, onWorkflowToggle, workflowPanelOpen,
  data, chatResponses,
  deptIcon: DeptIcon, deptLabel, deptColor,
  agentId, agentName, escalateLabel, escalateTarget,
  col2Icon: Col2Icon, col2Label, col2Items, col2Renderer,
  testIdPrefix,
  // Deep dive scripts (all optional)
  deepDiveScript,         // { opening, branches }
  alertDeepDives,          // { [alertId]: [turns] }
  decisionDeepDives,       // { [decisionId]: [turns] }
  rowDeepDives,            // { [rowKey]: [turns] }
  rowKeySelector,          // (item) => key — maps row item to rowDeepDives key
}) => {
  const [approvedDecisions, setApprovedDecisions] = useState(new Set());
  const [escalatedAlerts, setEscalatedAlerts] = useState(new Set());
  const [addressedAlerts, setAddressedAlerts] = useState(new Set());
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [deepDiveBranch, setDeepDiveBranch] = useState(null);
  const [deepDiveTurn, setDeepDiveTurn] = useState(0);
  const messagesEndRef = useRef(null);
  const timersRef = useRef([]);

  const tp = isDark ? 'text-white' : 'text-slate-800'; const ts = isDark ? 'text-white/50' : 'text-slate-500'; const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]'; const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]'; const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const trendUp = data.health.trend.startsWith('+');
  const deptHeadShortName = escalateTarget ? escalateTarget.split(' ')[0] : 'Lead';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const addTimer = (cb, delay) => { const id = setTimeout(cb, scaleDelay(delay)); timersRef.current.push(id); };
  const pushMessage = (msg) => setChatMessages((prev) => [...prev, msg]);
  const resetChat = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setDeepDiveBranch(null);
    setDeepDiveTurn(0);
    setChatMessages([]);
  };
  const lockPreviousChips = () => {
    setChatMessages((prev) => prev.map((m) => (m.chips ? { ...m, chipsDisabled: true } : m)));
  };

  const applyRipple = (ripple) => {
    if (!ripple) return;
    if (ripple.approveDecision) {
      setApprovedDecisions((prev) => { const next = new Set(prev); next.add(ripple.approveDecision); return next; });
    }
    if (ripple.actionAlert) {
      setAddressedAlerts((prev) => { const next = new Set(prev); next.add(ripple.actionAlert); return next; });
    }
  };

  const appendConversationClosure = () => {
    addTimer(() => pushMessage({ role: 'user', text: `Perfect. Thanks, ${agentName} — keep me posted.` }), 2000);
    addTimer(() => pushMessage({ role: 'thinking' }), 2600);
    addTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: 'Will do. Talk soon.' }];
      });
    }, 3800);
    addTimer(() => pushMessage({ role: 'complete-pill' }), 4600);
  };

  const normalizeTurn = (turn) => ({
    role: 'agent',
    text: turn.text || null,
    chips: turn.chips || null,
    executionSteps: turn.executionSteps || null,
    routedTask: turn.routedTask || null,
    closure: turn.closure || null,
  });

  /* ── Auto-play flat script (alerts, decisions, rows) ── */
  const autoPlayScript = (turns) => {
    const playNext = (idx) => {
      if (idx >= turns.length) return;
      const turn = turns[idx];
      if (turn.role === 'user') {
        addTimer(() => { pushMessage({ role: 'user', text: turn.text }); playNext(idx + 1); }, 1400);
        return;
      }
      if (turn.role === 'complete-pill') {
        addTimer(() => { pushMessage({ role: 'complete-pill' }); playNext(idx + 1); }, 700);
        return;
      }
      addTimer(() => pushMessage({ role: 'thinking' }), 500);
      const textPause = 2100 + (turn.text ? Math.min(turn.text.length * 3, 1200) : 0);
      addTimer(() => {
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, {
            role: 'agent',
            text: turn.text,
            executionSteps: turn.executionSteps || null,
            routedTask: turn.routedTask || null,
            closure: turn.closure || null,
          }];
        });
        applyRipple(turn.ripple);
        playNext(idx + 1);
      }, textPause);
    };
    playNext(0);
  };

  /* ── Header Deep Dive (chip-driven branching) ── */
  const handleDeepDive = () => {
    if (!deepDiveScript) {
      // Fallback to simple sendMessage
      setChatMessages([
        { role: 'user', text: `Give me a strategic deep dive on ${deptLabel}.` },
        { role: 'agent', text: chatResponses.deepDive.text, actions: chatResponses.deepDive.actions, timeline: chatResponses.deepDive.timeline },
      ]);
      setChatMode(true);
      return;
    }
    resetChat();
    const opening = deepDiveScript.opening;
    setChatMessages([{ role: 'user', text: opening.ceo }]);
    setChatMode(true);
    addTimer(() => pushMessage({ role: 'thinking' }), 500);
    addTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: opening[agentId], chips: opening.chips }];
      });
    }, 2000);
  };

  const handleChipClick = (chip) => {
    lockPreviousChips();
    pushMessage({ role: 'user', text: chip.label });
    if (!deepDiveScript) return;
    if (!deepDiveBranch) {
      const branchKey = chip.id;
      const turns = deepDiveScript.branches[branchKey];
      if (!turns) return;
      setDeepDiveBranch(branchKey);
      setDeepDiveTurn(0);
      addTimer(() => pushMessage({ role: 'thinking' }), 500);
      addTimer(() => {
        const first = turns[0];
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, normalizeTurn(first)];
        });
      }, 1800);
      return;
    }
    const turns = deepDiveScript.branches[deepDiveBranch];
    if (!turns) return;
    const nextIdx = deepDiveTurn + 1;
    setDeepDiveTurn(nextIdx);
    const nextTurn = turns[nextIdx];
    if (!nextTurn) return;
    const resolved = nextTurn.role === 'meta' && nextTurn.perChip ? nextTurn.perChip[chip.id] : nextTurn;
    if (!resolved) return;
    addTimer(() => pushMessage({ role: 'thinking' }), 400);
    addTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, normalizeTurn(resolved)];
      });
      applyRipple(resolved.ripple);
    }, 1600);
    if ((resolved.routedTask || resolved.closure) && !resolved.chips) {
      addTimer(() => appendConversationClosure(), 1800);
    }
  };

  /* ── Alert / Decision / Row Deep Dives (auto-played flat scripts) ── */
  const handleAlertDive = (alert) => {
    const script = alertDeepDives && alert?.id ? alertDeepDives[alert.id] : null;
    if (script) {
      resetChat();
      setChatMode(true);
      autoPlayScript(script);
      return;
    }
    // Fallback
    setChatMessages([
      { role: 'user', text: `Tell me more about "${alert.title}".` },
      { role: 'agent', text: `Here's my analysis.\n\n${alert.description}`, actions: alert.actions, timeline: `${agentName} will track progress and update you.` },
    ]);
    setChatMode(true);
  };

  const handleDecisionDeepDive = (decisionId) => {
    const script = decisionDeepDives ? decisionDeepDives[decisionId] : null;
    if (script) {
      resetChat();
      setChatMode(true);
      autoPlayScript(script);
      return;
    }
    const d = data.decisions.find(x => x.id === decisionId);
    if (!d) return;
    setChatMessages([
      { role: 'user', text: `Walk me through "${d.title}" — why does this need my approval?` },
      { role: 'agent', text: `Here's the context.\n\n${d.detail}\n\nI'm ready to execute the moment you approve.`, timeline: `${agentName} will proceed only after your sign-off.` },
    ]);
    setChatMode(true);
  };

  const handleRowDeepDive = (item) => {
    const key = rowKeySelector ? rowKeySelector(item) : item.id;
    const script = rowDeepDives ? rowDeepDives[key] : null;
    if (script) {
      resetChat();
      setChatMode(true);
      autoPlayScript(script);
      return;
    }
    const title = item.title || item.label || 'this item';
    setChatMessages([
      { role: 'user', text: `Give me a deep dive on "${title}".` },
      { role: 'agent', text: `Here's my read on ${title}.\n\nThis is currently owned by ${item.owner || agentName}.`, timeline: `${agentName} is tracking this.` },
    ]);
    setChatMode(true);
  };

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || chatResponses.general.text, actions: resp?.actions, timeline: resp?.timeline }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const handleEscalate = (alertId) => {
    setEscalatedAlerts(prev => new Set([...prev, alertId]));
    const alert = data.alerts.find(a => a.id === alertId);
    sendMessage(`Escalate "${alert.title}" to ${escalateTarget} with context.`, {
      text: `Done. I've flagged this to ${escalateTarget} with full context and recommended actions.`,
      actions: [{ agent: agentId, label: `Notified ${escalateTarget}: "${alert.title}" — flagged by Arjun`, status: 'complete' }, ...alert.actions.map(a => ({ ...a, status: 'recommended' }))],
      timeline: `${escalateTarget} will see this in their next briefing. ${agentName} will track progress.`,
    });
  };

  const handleApproveDecision = (decisionId) => {
    setApprovedDecisions(prev => new Set([...prev, decisionId]));
    const d = data.decisions.find(x => x.id === decisionId);
    sendMessage(`Approve: "${d.title}"`, {
      text: `Approved. I'm executing now.\n\n${d.detail}`,
      actions: [{ agent: agentId, label: `Executing: ${d.title}`, status: 'in-progress' }, { agent: agentId, label: `${escalateTarget} notified of approval`, status: 'complete' }],
      timeline: `${agentName} will confirm execution within the hour.`,
    });
  };

  const handleInput = (e) => { const ta = e.target; ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 200) + 'px'; setMessage(e.target.value); };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={handleInput} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={`Message ${agentName} about ${deptLabel}...`} rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid={`${testIdPrefix}-chat-input`} />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS[agentId]} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>{agentName}</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}><Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span></button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid={`${testIdPrefix}-send-btn`}>
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid={`${testIdPrefix}-chat-view`}>
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => { setChatMode(false); resetChat(); }} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid={`${testIdPrefix}-back-btn`}>
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to {deptLabel}</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <style>{`@keyframes deptFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <div className="flex justify-center">
            <div className="w-[80%] py-6">
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ marginTop: i > 0 ? 32 : 0 }}>
                  <ChatMessage msg={msg} isDark={isDark} agentId={agentId} agentName={agentName} onChipClick={handleChipClick} chipsDisabled={msg.chipsDisabled} />
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid={`${testIdPrefix}-page`}>
      {/* Health header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-5">
          <ScoreRing score={data.health.score} size={68} strokeWidth={4} isDark={isDark} status={data.health.status} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <DeptIcon className={`w-4 h-4 ${deptColor}`} />
              <p className={`text-sm font-semibold ${tp}`}>{deptLabel}</p>
              <div className="flex items-center gap-1">
                {trendUp ? <TrendingUp className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} /> : <TrendingDown className={`w-3 h-3 ${isDark ? 'text-red-400' : 'text-red-500'}`} />}
                <span className={`text-[11px] ${trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{data.health.trend} pts</span>
              </div>
            </div>
            <p className={`text-[11px] mt-0.5 ${ts}`}>{data.health.summary}</p>
          </div>
          <button onClick={handleDeepDive} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`} data-testid={`${testIdPrefix}-deep-dive-btn`}>
            <Search className="w-3.5 h-3.5" /> Deep Dive
          </button>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-3 gap-5 h-full px-5 py-4">

          {/* Col 1: Agents & Alerts */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <Bot className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>{deptLabel} Agents</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>{data.agents.filter(a => a.status === 'active').length} active</span>
            </div>
            <div className="flex flex-col gap-2 mb-4">{data.agents.map(a => <AgentCard key={a.id} agent={a} isDark={isDark} />)}</div>

            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Alerts</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-red-400/10 text-red-400' : 'bg-red-600/10 text-red-600'}`}>{data.alerts.length}</span>
            </div>
            <div className="flex flex-col gap-2">{data.alerts.map(a => <AlertCard key={a.id} alert={a} isDark={isDark} onDeepDive={handleAlertDive} onEscalate={handleEscalate} escalated={escalatedAlerts.has(a.id)} addressed={addressedAlerts.has(a.id)} escalateLabel={escalateLabel} deptHeadShortName={deptHeadShortName} />)}</div>
          </div>

          {/* Col 2: Domain items & Decisions */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <Col2Icon className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>{col2Label}</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>{col2Items.length}</span>
            </div>
            <div className="flex flex-col gap-2 mb-4">{col2Items.map((item, i) => col2Renderer(item, i, isDark, handleRowDeepDive))}</div>

            <div className="flex items-center gap-2 mb-3">
              <Shield className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Needs Your Decision</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>{data.decisions.filter(d => !approvedDecisions.has(d.id)).length}</span>
            </div>
            <div className="flex flex-col gap-2">{data.decisions.map(d => <DecisionCard key={d.id} item={d} isDark={isDark} onApprove={handleApproveDecision} onDeepDive={handleDecisionDeepDive} approved={approvedDecisions.has(d.id)} />)}</div>
          </div>

          {/* Col 3: Metrics & Activity */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Key Metrics</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {data.metrics.map((m, i) => (
                <div key={i} className={`px-2.5 py-2 rounded-lg border ${m.alert ? (isDark ? 'border-red-500/15 bg-red-500/5' : 'border-red-200/50 bg-red-50/50') : `${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}`}>
                  <p className={`text-[9px] ${ts}`}>{m.label}</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-base font-bold ${m.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{m.value}</span>
                    <span className={`text-[10px] font-medium ${m.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{m.trend}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Zap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Agent Activity</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>Live</span>
            </div>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'}`}>
              {data.recentActivity.map(item => <ActivityItem key={item.id} item={item} isDark={isDark} />)}
            </div>
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4CeoDeptTemplate;
